import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { ChangelogFeedEntry, ChangelogFeedResponseSchema } from '../domainTypes/changelog';
import { environment } from '../../environments/environment';

const paths = {
    feed: `${environment.apiBase}/changelog/feed`,
    seen: `${environment.apiBase}/changelog/seen`,
};

/** Backend-acked "what's new" feed state. fetch() loads all published entries
 *  (newest first); entries newer than the user's Seen watermark carry is_new.
 *  The dialog auto-opens only when at least one entry is new; open() forces it
 *  open for the account-menu "What's new?" affordance even when nothing is new. */
@Injectable({ providedIn: 'root' })
export class ChangelogService {
    private readonly http = inject(HttpClient);

    readonly entries = signal<ChangelogFeedEntry[]>([]);
    /** Server-reported: has this account ever been stamped with a Seen watermark. */
    private readonly hasWatermark = signal(false);
    /** True when at least one entry is newer than the Seen watermark. Drives auto-open. */
    readonly hasNew = computed(() => this.entries().some(e => e.is_new));
    /** True when the account has never been baselined (no Seen watermark). We
     *  DON'T auto-open in that case: a first-timer shouldn't be walled by a
     *  "what's new since you last used it" feed when they've never used it. The
     *  dialog silently baselines them instead, and genuinely-new future entries
     *  auto-open normally.
     *
     *  This comes from the server's has_watermark, NOT from "every entry is
     *  new". The feed is page-capped, so a returning user with more than a page
     *  of unseen entries also has every entry flagged new, and treating that as
     *  first-time-ness silently baselined them past updates they never saw
     *  (audit-4 #37). */
    readonly allNew = computed(() => this.entries().length > 0 && !this.hasWatermark());
    /** Set by open(); lets the dialog show even when nothing is new. */
    readonly openRequested = signal(false);
    /** True when the last fetch failed. Distinct from "the feed is empty": both
     *  leave entries() empty, but only one of them should tell the user they are
     *  caught up. Without this the dialog cannot tell the two apart and renders
     *  the same blank body for a network error as for a genuinely empty feed. */
    readonly loadFailed = signal(false);

    fetch(): Observable<void> {
        return this.http.get<unknown>(paths.feed).pipe(
            map(raw => ChangelogFeedResponseSchema.parse(raw)),
            tap(parsed => {
                this.entries.set(parsed.entries);
                this.hasWatermark.set(parsed.has_watermark);
                this.loadFailed.set(false);
            }),
            map(() => undefined),
            catchError(() => {
                this.entries.set([]);
                this.hasWatermark.set(false);
                this.loadFailed.set(true);
                return of(undefined);
            })
        );
    }

    /** Manual open (account-menu "What's new?"): refresh the feed, then flag the
     *  open request so the dialog shows the feed even when nothing is new. */
    open(): void {
        this.fetch().subscribe(() => this.openRequested.set(true));
    }

    /** Clear session state (called on logout) so a stale feed can't show for the
     *  next login before its fetch resolves. */
    reset(): void {
        this.entries.set([]);
        this.hasWatermark.set(false);
        this.openRequested.set(false);
        this.loadFailed.set(false);
    }

    /** Ack: called on ANY close. Optimistically clears is_new locally so a
     *  double-fire can't double-post, and only POSTs when something was actually
     *  new (the endpoint is idempotent regardless). Also clears the open request.
     *
     *  Sends up_to = the newest published_date we actually rendered, so an entry
     *  published between this fetch and this ack is not stamped as seen and
     *  vanished (audit-4 #38). */
    markSeen(): Observable<void> {
        const hadNew = this.hasNew();
        const upTo = this.entries().reduce<string | null>(
            (max, e) => (max === null || e.published_date > max ? e.published_date : max),
            null
        );
        this.entries.update(list => list.map(e => (e.is_new ? { ...e, is_new: false } : e)));
        this.hasWatermark.set(true);
        this.openRequested.set(false);
        if (!hadNew) {
            return of(undefined);
        }
        return this.http.post<void>(paths.seen, upTo ? { up_to: upTo } : {}).pipe(
            map(() => undefined),
            catchError(() => of(undefined))
        );
    }
}
